// @ts-check
import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  CloseIcon,
  Flex,
  Link,
  ReportsIcon,
  Text,
} from '~/design-system';
import { Trans } from '@lingui/macro';
import React from 'react';
import { useParams } from 'react-router-dom';
import { UPDATE_COLLECTION_ID } from '../ChooseCollection/ChooseCollection';
import Notification from '../Notification';
import useUploadFile, { UPLOAD_FILE } from '../useUploadFile';
import { useFileAttachment } from '../SmartImportPicker/FileAttachmentContext';

/**
 * Step where the file is selected
 * @param {ReturnType<useChooseFile>} props
 */
export default function ChooseFile({
  onDelete,
  file,
  isUploadingFile,
  notification,
  cancelNotification,
  dropzoneState: { getRootProps, getInputProps, isDragActive, inputRef },
}) {
  return (
    <React.Fragment>
      <Box
        sx={{
          margin: '0 auto',
          width: '623px',
        }}
      >
        <input {...getInputProps()} />
        {!file ? (
          <Flex
            align="center"
            {...getRootProps()}
            sx={{
              border: isDragActive ? '1px solid' : '1px solid',
              borderColor: isDragActive ? 'brand.hoverBlue' : 'brand.gray',
              backgroundColor: 'brand.lightGray',
              height: 100,
              width: '100%',
              textAlign: 'center',
            }}
            onClick={undefined}
          >
            {!isUploadingFile ? (
              <FileChooser inputRef={inputRef} />
            ) : (
              <Uploading />
            )}
          </Flex>
        ) : (
          <FilePreview onDelete={onDelete} file={file} />
        )}
      </Box>

      {notification && (
        <Notification
          onClose={() => cancelNotification()}
          message={notification.message}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

function FileChooser({ inputRef }) {
  return (
    <Box sx={{ width: '100%' }}>
      <Text>
        <Trans>
          Drag and drop or{' '}
          <Link
            onClick={() => {
              inputRef.current.click();
            }}
          >
            choose a file
          </Link>{' '}
          to upload.
        </Trans>
      </Text>
      <Text>
        <Trans>All .xlsx file types are supported.</Trans>
      </Text>
    </Box>
  );
}

function FilePreview({ onDelete, file }) {
  return (
    <Flex
      align="center"
      sx={{
        justifyContent: 'center',
        position: 'relative', // ensuring CloseIcon renders inside
        border: '1px solid',
        borderColor: 'brand.gray',
        backgroundColor: 'brand.lightGray',
        height: 100,
      }}
    >
      <Button
        variant="text"
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
        }}
        onClick={() => {
          onDelete({ fileId: file.fileId });
        }}
      >
        <CloseIcon size="xxsmall" />
      </Button>
      <Text>
        <ReportsIcon sx={{ marginRight: 1, marginBottom: 1 }} /> {file.name}
      </Text>
    </Flex>
  );
}

function Uploading() {
  return (
    <Box sx={{ width: '100%' }}>
      <Text>
        <Trans>Uploading...</Trans>
      </Text>
    </Box>
  );
}

/**
 *
 * @param {object} props
 * @param {import('../../pages/imports/_config').DataImportType} props.importType
 * @param {string | null} [props.collectionId] - if provided will be saved on importData
 * @returns
 */
export function useChooseFile({ importType, collectionId }) {
  const { workspaceId } = useParams();
  const [isUploadingFile, setIsUploadingFile] = React.useState(false);
  const [importId, setImportId] = React.useState(
    /** @type {string | null | undefined}*/ (undefined)
  );
  const [updateImportCollectionId] = useMutation(UPDATE_COLLECTION_ID);
  const [uploadFromAttachment] = useMutation(UPLOAD_FILE);
  const [notification, setNotification] = React.useState(
    /** @type {import('../Notification').Notification | undefined} */
    (undefined)
  );

  const [deleteDataImport] = useMutation(DELETE_IMPORT);

  const [file, setFile] = React.useState(
    /** @type {File | null | undefined} */ (undefined)
  );

  const { consumePendingFile } = useFileAttachment();
  const consumedRef = React.useRef(false);

  async function onDelete({ fileId }) {
    const { data } = await deleteDataImport({
      variables: { importId: fileId },
    });
    if (!data?.result?.success) {
      setNotification({
        message: 'There was an error, please try again!',
      });
    }
    setFile(undefined);
  }

  const dropzoneState = useUploadFile({
    onUpload: (data) => {
      setIsUploadingFile(false);
      if (data.success) {
        if (!data.file?.fileId) return;
        // @ts-ignore we ignore missing names.
        setFile(data.file);
        if (collectionId) {
          updateImportCollectionId({
            variables: { importId: data.file.fileId, collectionId },
          });
        }
        setImportId(data.file.fileId);
      } else {
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
    },
    onDrop: () => {
      setIsUploadingFile(true);
      setImportId(undefined);
    },
    type: importType,
  });

  React.useEffect(() => {
    if (consumedRef.current) return;
    if (!workspaceId) return;
    const pending = consumePendingFile();
    if (!pending) return;
    consumedRef.current = true;
    setIsUploadingFile(true);
    setImportId(undefined);
    uploadFromAttachment({
      variables: { workspaceId, file: pending, type: importType },
    })
      .then(({ data }) => {
        const result = data?.result;
        setIsUploadingFile(false);
        if (result?.success && result.file?.fileId) {
          // @ts-ignore we ignore missing names.
          setFile(result.file);
          if (collectionId) {
            updateImportCollectionId({
              variables: { importId: result.file.fileId, collectionId },
            });
          }
          setImportId(result.file.fileId);
        } else {
          setNotification({
            message: 'There was an error, please try again!',
          });
        }
      })
      .catch(() => {
        setIsUploadingFile(false);
        setNotification({
          message: 'There was an error, please try again!',
        });
      });
  }, [
    workspaceId,
    importType,
    collectionId,
    consumePendingFile,
    uploadFromAttachment,
    updateImportCollectionId,
  ]);

  return {
    isValid: !!importId,
    onDelete,
    file,
    isUploadingFile,
    importId,
    notification,
    cancelNotification: () => {
      setNotification(undefined);
    },
    dropzoneState,
    importType,
  };
}

/**
 * @typedef {object} File
 * @property {string} fileId
 * @property {boolean} [isLive]
 * @property {String[]} [languages]
 * @property {String} name
 * @property {object} [thumbnail]
 */

/** @type {typeof import('./ChooseFile.generated').DeleteDataImportDocument} */
export const DELETE_IMPORT = gql`
  mutation deleteDataImport($importId: ID!) {
    result: deleteDataImport(input: { importId: $importId }) {
      code
      success
      message
    }
  }
`;
